import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  CompanyFormKeys,
  SignupService,
} from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import UIDefaultTheme from 'src/themes';
import Utils from 'src/app/helpers/utils';
import { StorageService } from 'src/app/services/storage/storage.service';

@Component({
  selector: 'app-signup-unified-company',
  templateUrl: './signup-unified-company.component.html',
  styleUrls: ['./signup-unified-company.component.scss'],
})
export class SignupUnifiedCompanyComponent implements OnInit {
  steps: Step[] = [
    {
      icon: 'fas fa-file-alt',
      svg: 'assets/images/icon-inputdata-active.svg',
      title: 'Dados complementares',
      slug: 'complementary',
      substeps: [
        {
          title: 'Geral',
          slug: 'complementaryGeneral',
        },
        {
          title: 'Endereço',
          slug: 'complementaryAddress',
        },
        {
          title: 'Dados bancários',
          slug: 'complementaryBank',
        },
        {
          title: 'Composição acionária',
          slug: 'complementaryComposition',
        },
        {
          title: 'Grupo econômico',
          slug: 'complementaryEconomic',
        },
        {
          title: 'Fundos de interesse',
          slug: 'complementaryBond',
        },
        {
          title: 'Informações de risco',
          slug: 'complementaryRisk',
        },
        {
          title: 'Representantes',
          slug: 'complementaryRepresentatives',
        },
      ],
    },
    {
      icon: 'fas fa-file-alt',
      title: 'Resumo dos poderes',
      svg: 'assets/images/icon-inputdata-active.svg',
      slug: 'powers',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/icon-attachments.svg',
      title: 'Anexos & comprovantes',
      slug: 'attachments',
    },
  ];

  shareholderSteps: Step[] = [
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Rendimentos & patrimônios',
      slug: 'income',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declarações & autorizações',
      slug: 'declarations',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Análise de perfil de investidor',
      slug: 'analysis',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declaração de Condição de Investidor',
      slug: 'conditions',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declaração US Person / Non US Person (FATCA)',
      slug: 'tax',
    },
  ];

  userCredentials: UserCredentials = {
    email: '',
    name: '',
  };

  userOptionsOpen: boolean = false;

  isShareholder = false;

  _activeStep: number = 0;

  activeSubstep: ComplementaryCompanySteps | null = null;

  redirected = false;
  hasMultipleAccounts = false;
  showLoadingSpinner = true;

  get activeStep() {
    return this._activeStep;
  }

  set activeStep(value) {
    if (this._activeStep !== value) {
      // Scroll to top when step changes
      try {
        this.viewportScroller.scrollToPosition([0, 0]);
      } catch (error) {
        // Ignore scroll errors during initialization
        console.warn('Scroll error:', error);
      }
    }
    this._activeStep = value;

    // Define currentTab apenas quando estiver na etapa de anexos
    let isAttachmentsStep = false;

    if (this.isShareholder) {
      if (this.isFund && value === 4) {
        isAttachmentsStep = true;
      } else if (value === 7) {
        isAttachmentsStep = true;
      }
    } else {
      if (value === 2) {
        isAttachmentsStep = true;
      }
    }

    this.currentTab = isAttachmentsStep ? 'Anexos & comprovantes' : '';
  }

  currentTab: string = '';

  showSupportModal = false;

  name: string | null = null;
  role: string | null = null;
  label: string | null = null;

  agentOptions: SelectItem[] = [];

  isFund = false;
  isProvider = false;
  routeId: string = null;

  accounts: RegisterUserResponse[] = [];

  public UIDefaultTheme = UIDefaultTheme;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public signupService: SignupService,
    private apiService: ApiService,
    private toastService: ToastService,
    private authService: AuthService,
    private titleService: Title,
    private storageService: StorageService,
    private viewportScroller: ViewportScroller,
  ) {
    if (this.authService.user.active_register.register.role.slug) {
      this.titleService.setTitle(
        Utils.setTitle(
          this.authService.user.active_register.register.role.slug,
        ),
      );
    }
  }

  ngOnInit(): void {
    // Comparar o UUID da URL com o id do active_register.register
    this.routeId = this.route.snapshot.paramMap.get('id');
    const currentRegisterId =
      this.authService.user?.active_register?.register?.id;

    if (
      this.routeId &&
      currentRegisterId &&
      this.routeId !== currentRegisterId
    ) {
      // Está editando outro register (não é o active_register atual)
      this.hasMultipleAccounts = true;
    } else {
      // Está editando o próprio register ou não há routeId
      this.hasMultipleAccounts = false;
    }

    this.isShareholder = this.router.url.includes('shareholder');
    this.isProvider = this.router.url.includes('provider');
    const isAssignor = this.router.url.includes('assignor');

    // Remover etapa de risco para cedentes e prestadores de serviço
    if (isAssignor || this.isProvider) {
      const complementaryStep = this.steps.find(
        (step) => step.slug === 'complementary',
      );
      if (complementaryStep && complementaryStep.substeps) {
        const riskIndex = complementaryStep.substeps.findIndex(
          (substep) => substep.slug === 'complementaryRisk',
        );
        if (riskIndex !== -1) {
          complementaryStep.substeps.splice(riskIndex, 1);
        }
      }
    }

    /**
     * Inicializar isFund SEM usar o active_register para detectar PJ-FI.
     * A fonte da verdade passa a ser sempre o endpoint `required-files`,
     * chamado em `updateIsFundFromRegisterId`.
     */
    this.isFund = false;
    this.signupService.setIsFund(false);

    this.updateIsFundFromRegisterId(this.routeId);

    if (this.isShareholder) {
      this.steps.splice(2, 0, ...this.shareholderSteps);

      // A configuração específica de fundo é feita quando `updateIsFundFromRegisterId`
      // terminar e atualizar `isFund` / serviço, chamando `configureStepsForFund()`.
    }

    const firstName = this.authService.user?.first_name || '';
    const lastName = this.authService.user?.last_name || '';
    const fullName = [firstName, lastName]
      .filter((name) => name && name.trim() !== '')
      .join(' ')
      .trim();

    this.userCredentials = {
      email: this.authService.user?.email,
      name: fullName || '',
    };

    document.onkeydown = (e) => {
      if (e.key === 'Enter') {
        this.proceed();
      }
    };

    this.signupService.changeCompanyStepEvent.subscribe((step) => {
      const aux = `complementary${
        step.charAt(0).toUpperCase() + step.slice(1)
      }` as ComplementaryCompanySteps;

      this.activeSubstep = aux;
      this.signupService.loading = false;
      this.activeStep = 0;

      this.signupService.updateUserStepCompany('register', step, this.routeId);
    });

    this.signupService.changeBasicInfoEvent.subscribe(async (value) => {
      this.label = await this.getRoleLabel(this.role);

      this.name = value.toUpperCase();
    });

    this.signupService.fetchDataEvent.emit();

    let urlParts = this.router.url.split('/pj')[0].split('/');
    let range = urlParts.length;
    this.role = urlParts[range - 1];

    if (this.role === 'assignor') {
      // TODO mudar a logica - replace representative step title
      this.steps[0].substeps[6].title =
        'Representantes e Avalistas/Dev. Solidários';
    }

    this.getData();
  }

  async getData() {
    this.showLoadingSpinner = true;
    try {
      // setInviteInfo é async, precisa ser aguardado para garantir que basicInfo seja definido antes dos componentes filhos
      await this.setInviteInfo();

      // Chamar getAgents em paralelo com o patch/get condicional (se necessário)
      // pois são independentes entre si
      const promises: Promise<any>[] = [this.getAgents()];

      // Aguardar getAgents e patch/get (se necessário) terminarem
      await Promise.all(promises);

      // redirectUser pode ser chamado após o patch/get condicional (se necessário)
      // pois pode depender do active_register atualizado
      await this.redirectUser();

      this.redirected = true;
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  getUserInitials(string) {
    if (!string || typeof string !== 'string' || string.trim() === '') {
      return '';
    }
    return string
      .toUpperCase()
      .replace(/[^\w\s]/gi, '')
      .split(' ')
      .filter(
        (word: string) =>
          word && word.trim() !== '' && word.toLowerCase() !== 'null',
      )
      .slice(0, 2)
      .map((word: string) => word.charAt(0))
      .join('');
  }

  async getAgents() {
    try {
      const res = await this.apiService.get({
        route: 'api/registration/simplified-agent/',
      });

      this.agentOptions = res.map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
    } catch (error) {
      console.warn(error);
    }
  }

  get hasSelectedRoleId(): boolean {
    const selectedRoleId = this.storageService.get('selected_role_id', true);
    return selectedRoleId !== null && selectedRoleId !== undefined;
  }

  async redirectToDash() {
    const selectedRoleId = this.storageService.get('selected_role_id', true);

    if (selectedRoleId && this.routeId && this.role) {
      this.storageService.remove('selected_role_id');

      // Se está em adição de papel, redirecionar para tela de detalhes
      this.router.navigateByUrl(`/app/details/${this.routeId}`);
    } else {
      // Comportamento padrão: ir para dashboard
      this.storageService.remove('selected_role_id');
      this.router.navigateByUrl('/app/dashboard');
    }
  }

  async redirectUser() {
    try {
      // Sempre usar o register_id da URL (editingRegisterId) ou da rota

      if (!this.routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      // Garante que isFund esteja resolvido antes de mapear substeps (especialmente para o shareholder PJ-FI).
      await this.updateIsFundFromRegisterId(this.routeId);

      // Sempre buscar trace_step usando o register_id da URL
      const response = await this.apiService.get({
        route: 'api/registration/register-step/',
        params: { register_uuid: this.routeId },
        token: true,
      });

      let { data } = response;

      if (!data) {
        data = { step: 'register', substep: 'general' };
      }

      if (data.step === 'review') {
        data.step = 'register';
        data.substep = 'general';
      }

      // Se for cedente ou prestador e o backend retornar etapa de risco, redirecionar para representantes
      const isAssignor = this.router.url.includes('assignor');
      if ((isAssignor || this.isProvider) && data.substep === 'risk') {
        data.substep = 'representatives';
      }

      this.signupService.setComplementaryCompanyStep(data.substep);

      // Atualizar isFund se estivermos editando outro register
      // Buscar o role do register se necessário
      if (this.hasMultipleAccounts && this.routeId) {
        await this.updateIsFundFromRegisterId(this.routeId);
      }

      this.setBasicInfo();

      // TODO mudar essa lógica

      if (data.step === 'register') {
        if (this.isShareholder) {
          if (this.isFund) {
            switch (data.substep) {
              case 'powers':
                this.activeStep = 1;
                this.activeSubstep = null;
                break;
              case 'declarations':
                this.activeStep = 2;
                this.activeSubstep = null;
                break;
              case 'tax':
                this.activeStep = 3;
                this.activeSubstep = null;
                break;
              case 'attachments':
                this.activeStep = 4;
                this.activeSubstep = null;
                break;
              default:
                this.activeSubstep = `complementary${
                  data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
                }` as ComplementaryCompanySteps;
                this.signupService.setComplementaryCompanyStep(
                  this.activeSubstep,
                );
                this.activeStep = 0;
                break;
            }
          } else {
            switch (data.substep) {
              case 'powers':
                this.activeStep = 1;
                this.activeSubstep = null;
                break;
              case 'income':
                this.activeStep = 2;
                this.activeSubstep = null;
                break;
              case 'declarations':
                this.activeStep = 3;
                this.activeSubstep = null;
                break;
              case 'analysis':
                this.activeStep = 4;
                this.activeSubstep = null;
                break;
              case 'conditions':
                this.activeStep = 5;
                this.activeSubstep = null;
                break;
              case 'tax':
                this.activeStep = 6;
                this.activeSubstep = null;
                break;
              case 'attachments':
                this.activeStep = 7;
                this.activeSubstep = null;
                break;
              case 'risk':
                // Se for cedente ou prestador e tentar acessar etapa de risco, redirecionar para representantes
                const isAssignorRisk = this.router.url.includes('assignor');
                if (isAssignorRisk || this.isProvider) {
                  this.activeSubstep = 'complementaryRepresentatives';
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryRepresentatives',
                  );
                  this.activeStep = 0;
                } else {
                  this.activeSubstep = `complementary${
                    data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
                  }` as ComplementaryCompanySteps;
                  this.signupService.setComplementaryCompanyStep(
                    this.activeSubstep,
                  );
                  this.activeStep = 0;
                }
                break;
              default:
                this.activeSubstep = `complementary${
                  data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
                }` as ComplementaryCompanySteps;
                this.signupService.setComplementaryCompanyStep(
                  this.activeSubstep,
                );
                this.activeStep = 0;
                break;
            }
          }
        } else {
          if (data.substep === 'powers') {
            this.activeStep = 1;
            this.activeSubstep = null;
          } else if (data.substep === 'attachments') {
            this.activeStep = 2;
            this.activeSubstep = null;
          } else if (data.substep === 'risk') {
            // Se for cedente ou prestador e tentar acessar etapa de risco, redirecionar para representantes
            const isAssignorRisk = this.router.url.includes('assignor');
            if (isAssignorRisk || this.isProvider) {
              this.activeSubstep = 'complementaryRepresentatives';
              this.signupService.setComplementaryCompanyStep(
                'complementaryRepresentatives',
              );
              this.activeStep = 0;
            } else {
              this.activeSubstep = `complementary${
                data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
              }` as ComplementaryCompanySteps;
              this.signupService.setComplementaryCompanyStep(
                this.activeSubstep,
              );
              this.activeStep = 0;
            }
          } else {
            this.activeSubstep = `complementary${
              data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
            }` as ComplementaryCompanySteps;
            this.signupService.setComplementaryCompanyStep(this.activeSubstep);
            this.activeStep = 0;
          }
        }
      }

      if (data.step === 'review') {
        this.router.navigateByUrl(`/app/details/${this.role}/${this.routeId}`);
      }

      if (data.step !== 'register' && data.step !== 'review') {
        this.router.navigateByUrl(`/app/dashboard`);
      }
    } catch (error) {
      console.error('Erro ao carregar trace step:', error);
    }
  }

  async setBasicInfo() {
    try {
      // GET usa query parameter com register_uuid
      let apiRoute: string;
      let params: any = null;

      apiRoute = 'api/registration/company/general/';
      params = { register_uuid: this.routeId };

      const { data } = await this.apiService.get<ApiResponse<Company>>({
        route: apiRoute,
        params: params,
        token: true,
      });

      if (data && data.corporate_name !== null && data.corporate_name !== '') {
        this.label = await this.getRoleLabel(this.role);

        this.name = data.corporate_name.toUpperCase();
      }
    } catch (error) {
      console.log(error);
    }
  }

  async getRoleLabel(role: string): Promise<string> {
    try {
      // Buscar dados do register pela API
      const response = await this.apiService.get<any>({
        route: `api/registration/register/${this.routeId}`,
        token: true,
      });

      const register = response?.data;

      if (!register) {
        return '';
      }

      switch (role) {
        case 'assignor':
          return 'Cedente Pessoa Jurídica';

        case 'provider':
          let label = '';

          if (register.invite) {
            label = register.invite.data.agent
              .map((item) => {
                return this.agentOptions.find((agent) => agent.value === item)
                  .label;
              })
              .join(', ');
          } else if (register.agent && Array.isArray(register.agent)) {
            label = register.agent.map((item) => item.name).join(', ');
          }

          return label;

        default:
          return `Cotista ${register?.role?.name ?? ''}`;
      }
    } catch (error) {
      console.error(
        'Erro ao buscar dados do register para getRoleLabel:',
        error,
      );
      // Fallback em caso de erro
      const user = this.authService.getUser();
      switch (role) {
        case 'assignor':
          return 'Cedente Pessoa Jurídica';
        default:
          return `Cotista ${user.active_register?.register?.role?.name ?? ''}`;
      }
    }
  }

  async sendApproval() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      if (!this.routeId) {
        this.toastService.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota',
        );
        return false;
      }

      // Verificar se há um role_id no storage (papel selecionado no dropdown de edição)
      const selectedRoleId = this.storageService.get('selected_role_id', true);

      // Buscar dados atualizados do register da API para obter role_draft
      let roleDraft = null;
      try {
        const registerResponse = await this.apiService.get<any>({
          route: `api/registration/register/${this.routeId}`,
          token: true,
        });

        // role_draft pode ser um objeto ou um ID, pegar o ID
        const roleDraftData = registerResponse?.data?.role_draft;
        roleDraft = roleDraftData?.id || roleDraftData;
      } catch (error) {
        console.error('Erro ao buscar dados do register:', error);
        // Fallback para authService se a API falhar
        const fallbackRoleDraft =
          this.authService.user?.active_register?.register?.role_draft;
        roleDraft = fallbackRoleDraft?.id || fallbackRoleDraft;
      }

      // Priorizar role_id do storage (papel selecionado no dropdown) sobre role_draft
      const roleIdToUse = selectedRoleId || roleDraft;

      if (roleIdToUse) {
        // Se houver role_id (do storage ou role_draft), fazer as 3 chamadas na sequência:

        // 1. Primeiro PATCH: adicionar role_id
        try {
          await this.apiService.patch({
            route: `api/v3/registration/registers/`,
            body: {
              register_uuid: this.routeId,
              role_id: roleIdToUse,
            },
            token: true,
          });

          // Limpar o storage após usar o role_id
          if (selectedRoleId) {
            this.storageService.remove('selected_role_id');
          }
        } catch (error) {
          console.error('Erro ao adicionar role_id:', error);
          // Continua mesmo se falhar
        }

        // 2. POST send-to-approval
        await this.apiService.post({
          route: 'api/approvals/send-to-approval/',
          params: { register_uuid: this.routeId },
          body: {},
          token: true,
        });
        // await this.resetRepresentativesSignStatus(this.routeId);

        // Enviar emails para representantes após send-to-approval
        // try {
        //   await this.apiService.post({
        //     route: 'api/approvals/send-representative-emails/',
        //     params: { register_uuid: this.routeId },
        //     body: {},
        //     token: true,
        //   });
        // } catch (err) {
        //   console.error('Erro ao enviar emails para representantes:', err);
        // }

        // 3. Segundo PATCH: limpar role_draft
        try {
          await this.apiService.patch({
            route: `api/v3/registration/registers/`,
            body: {
              register_uuid: this.routeId,
              role_draft: null,
            },
            token: true,
          });
        } catch (error) {
          console.error('Erro ao limpar role_draft:', error);
          // Continua mesmo se falhar
        }
      } else {
        // Se não houver role_draft, apenas fazer send-to-approval (comportamento normal)
        await this.apiService.post({
          route: 'api/approvals/send-to-approval/',
          params: { register_uuid: this.routeId },
          body: {},
          token: true,
        });
        // await this.resetRepresentativesSignStatus(this.routeId);

        // Enviar emails para representantes após send-to-approval
        // try {
        //   await this.apiService.post({
        //     route: 'api/approvals/send-representative-emails/',
        //     params: { register_uuid: this.routeId },
        //     body: {},
        //     token: true,
        //   });
        // } catch (err) {
        //   console.error('Erro ao enviar emails para representantes:', err);
        // }

        // Criar versão depois do send-to-approval
        try {
          await this.apiService.post({
            route: 'api/approvals/create-version/',
            params: { register_uuid: this.routeId },
            body: {},
            token: true,
          });
        } catch (error) {
          console.error('Erro ao criar versão:', error);
          // Continua mesmo se falhar
        }
      }

      this.toastService.show(
        'info',
        'Sucesso',
        'Solicitação de aprovação enviada com sucesso',
      );

      return true;
    } catch (error) {
      if (error.error?.sync_error) {
        this.toastService.show(
          'warning',
          'Atenção',
          `Solicitação enviada, mas: ${error.error.sync_error}`,
        );
        this.activeStep--;
        return false;
      }

      this.toastService.show(
        'error',
        'Erro',
        'Falha ao enviar solicitação de aprovação ao sistema',
      );

      this.activeStep--;

      return false;
    }
  }

  canProceed() {
    if (!this.signupService) {
      return false;
    }

    if (this.isShareholder) {
      if (this.isFund) {
        switch (this.activeStep) {
          case 0:
            const step = this.signupService.getComplementaryCompanyStep();
            return this.signupService.getCompanyFormStatus(step);

          case 1:
            const powers = this.signupService.getCompanyFormStatus('powers');
            return powers;

          case 2:
            const declarations =
              this.signupService.getCompanyFormStatus('declarations');
            return declarations;

          case 3:
            const tax = this.signupService.getCompanyFormStatus('tax');
            return tax;

          case 4:
            const attachment =
              this.signupService.getCompanyFormStatus('attachments');
            return attachment;

          case 5:
            return true;
          default:
            return false;
        }
      } else {
        switch (this.activeStep) {
          case 0:
            const step = this.signupService.getComplementaryCompanyStep();
            return this.signupService.getCompanyFormStatus(step);

          case 1:
            const powers = this.signupService.getCompanyFormStatus('powers');
            return powers;

          case 2:
            const realy =
              this.signupService.getCompanyFormStatus('incomeRealty');
            const others =
              this.signupService.getCompanyFormStatus('incomeOthers');
            const income =
              this.signupService.getCompanyFormStatus('incomeSalary');

            return realy && income && others;

          case 3:
            const declarations =
              this.signupService.getCompanyFormStatus('declarations');
            return declarations;

          case 4:
            const analysis =
              this.signupService.getCompanyFormStatus('analysis');
            return analysis;

          case 5:
            const conditions =
              this.signupService.getCompanyFormStatus('conditions');
            return conditions;

          case 6:
            const tax = this.signupService.getCompanyFormStatus('tax');
            return tax;

          case 7:
            const attachment =
              this.signupService.getCompanyFormStatus('attachments');
            return attachment;

          case 8:
            return true;
          default:
            return false;
        }
      }
    } else {
      switch (this.activeStep) {
        case 0:
          const step = this.signupService.getComplementaryCompanyStep();
          return this.signupService.getCompanyFormStatus(step);

        case 1:
          const powers = this.signupService.getCompanyFormStatus('powers');
          return powers;

        case 2:
          const attachment =
            this.signupService.getCompanyFormStatus('attachments');
          return attachment;

        case 3:
          return true;
        default:
          return false;
      }
    }
  }

  private async resetRepresentativesSignStatus(register_uuid: string) {
    if (!register_uuid) {
      return;
    }

    try {
      const response = await this.apiService.get<{
        data: Array<{
          id: number;
          signs_registration_form?: boolean;
          is_registration_form_mandatory?: boolean;
        }>;
      }>({
        route: 'api/registration/registration-sign-status/',
        params: { register_uuid },
        token: true,
      });

      const representatives = (response?.data || []).filter(
        (item) =>
          item?.signs_registration_form || item?.is_registration_form_mandatory,
      );
      if (representatives.length === 0) {
        return;
      }

      await Promise.all(
        representatives.map((item) =>
          this.apiService.patch({
            route: `api/registration/registration-sign-status/${item.id}/`,
            params: { register_uuid },
            body: { has_signed: false },
            token: true,
          }),
        ),
      );
    } catch (error) {
      // Ignorar erro para não bloquear o fluxo de envio
    }
  }

  getButtonIcon(stepAction: string) {
    try {
      if (stepAction === 'next') {
        return this.canProceed()
          ? 'assets/images/icon_button_arrow.svg'
          : 'assets/images/icon_button_block.svg';
      } else {
        return this.activeStep === 0
          ? 'assets/images/icon_button_block.svg'
          : 'assets/images/icon_button_arrow.svg';
      }
    } catch (error) {
      return 'assets/images/icon_button_block.svg';
    }
  }

  toggleUserOptions() {
    this.userOptionsOpen = !this.userOptionsOpen;
  }

  setActiveStep(event: any) {
    this.activeStep = event;
    // Ensure scroll to top when clicking directly on stepper
    setTimeout(() => {
      this.viewportScroller.scrollToPosition([0, 0]);
    }, 0);
  }

  runBack() {
    // TODO mudar essa lógica
    if (this.isShareholder) {
      if (this.isFund) {
        switch (this.activeStep) {
          case 4:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'tax',
              this.routeId,
            );

            break;

          case 3:
            this.activeStep--;

            this.signupService.updateUserStepCompany(
              'register',
              'declarations',
              this.routeId,
            );
            break;

          case 2:
            this.activeStep--;

            this.signupService.updateUserStepCompany(
              'register',
              'powers',
              this.routeId,
            );
            break;

          case 1:
            this.activeStep--;

            this.signupService.changeCompanyStepEvent.emit('manager');

            this.signupService.setComplementaryCompanyStep(
              'complementaryManager',
            );

            break;

          case 0:
            if (!this.activeSubstep) break;
            switch (this.activeSubstep) {
              case 'complementaryAddress':
                this.signupService.changeCompanyStepEvent.emit('general');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryGeneral',
                );
                break;

              case 'complementaryBank':
                this.signupService.changeCompanyStepEvent.emit('address');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAddress',
                );
                break;

              case 'complementaryBond':
                this.signupService.changeCompanyStepEvent.emit('bank');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBank',
                );
                break;

              case 'complementaryRisk':
                this.signupService.changeCompanyStepEvent.emit('bond');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBond',
                );
                break;

              case 'complementaryRepresentatives':
                this.signupService.changeCompanyStepEvent.emit('risk');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryRisk',
                );
                break;

              case 'complementaryAdmin':
                this.signupService.changeCompanyStepEvent.emit(
                  'representatives',
                );

                this.signupService.setComplementaryCompanyStep(
                  'complementaryRepresentatives',
                );
                break;

              case 'complementaryManager':
                this.signupService.changeCompanyStepEvent.emit('admin');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAdmin',
                );
                break;

              default:
                break;
            }

            break;
          default:
            break;
        }
      } else {
        switch (this.activeStep) {
          case 7:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'tax',
              this.routeId,
            );
            break;

          case 6:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'conditions',
              this.routeId,
            );
            break;

          case 5:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'analysis',
              this.routeId,
            );

            break;

          case 4:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'declarations',
              this.routeId,
            );
            break;

          case 3:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'income',
              this.routeId,
            );
            break;

          case 2:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'powers',
              this.routeId,
            );
            break;

          case 1:
            this.activeStep--;

            this.signupService.changeCompanyStepEvent.emit('representatives');

            this.signupService.setComplementaryCompanyStep(
              'complementaryRepresentatives',
            );

            break;

          case 0:
            if (!this.activeSubstep) break;
            switch (this.activeSubstep) {
              case 'complementaryAddress':
                this.signupService.changeCompanyStepEvent.emit('general');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryGeneral',
                );
                break;

              case 'complementaryBank':
                this.signupService.changeCompanyStepEvent.emit('address');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAddress',
                );
                break;

              case 'complementaryComposition':
                this.signupService.changeCompanyStepEvent.emit('bank');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBank',
                );
                break;

              case 'complementaryEconomic':
                this.signupService.changeCompanyStepEvent.emit('composition');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryComposition',
                );
                break;

              case 'complementaryBond':
                this.signupService.changeCompanyStepEvent.emit('economic');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryEconomic',
                );
                break;

              case 'complementaryRisk':
                // Não deve acessar etapa de risco se for cedente ou prestador
                const isAssignorRisk = this.router.url.includes('assignor');
                if (isAssignorRisk || this.isProvider) {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                } else {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                }
                break;

              case 'complementaryRepresentatives':
                // Se for cedente ou prestador, pula a etapa de risco
                const isAssignorRep = this.router.url.includes('assignor');
                if (isAssignorRep || this.isProvider) {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                } else {
                  this.signupService.changeCompanyStepEvent.emit('risk');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryRisk',
                  );
                }
                break;

              case 'complementaryAdmin':
                this.signupService.changeCompanyStepEvent.emit(
                  'representatives',
                );

                this.signupService.setComplementaryCompanyStep(
                  'complementaryRepresentatives',
                );
                break;

              case 'complementaryManager':
                this.signupService.changeCompanyStepEvent.emit('admin');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAdmin',
                );
                break;

              default:
                break;
            }

            break;
          default:
            break;
        }
      }
    } else {
      if (this.isProvider) {
        switch (this.activeStep) {
          case 2:
            this.activeStep--;

            this.signupService.updateUserStepCompany(
              'register',
              'powers',
              this.routeId,
            );
            break;

          case 1:
            this.activeStep--;

            this.signupService.changeCompanyStepEvent.emit('representatives');

            this.signupService.setComplementaryCompanyStep(
              'complementaryRepresentatives',
            );
            break;

          case 0:
            if (!this.activeSubstep) break;
            switch (this.activeSubstep) {
              case 'complementaryAddress':
                this.signupService.changeCompanyStepEvent.emit('general');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryGeneral',
                );
                break;

              case 'complementaryBank':
                this.signupService.changeCompanyStepEvent.emit('address');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAddress',
                );
                break;

              case 'complementaryComposition':
                this.signupService.changeCompanyStepEvent.emit('bank');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBank',
                );
                break;

              case 'complementaryEconomic':
                this.signupService.changeCompanyStepEvent.emit('composition');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryComposition',
                );
                break;

              case 'complementaryBond':
                this.signupService.changeCompanyStepEvent.emit('economic');
                this.signupService.setComplementaryCompanyStep(
                  'complementaryEconomic',
                );
                break;

              case 'complementaryRepresentatives':
                this.signupService.changeCompanyStepEvent.emit('bond');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBond',
                );
                break;

              default:
                break;
            }

            break;
          default:
            break;
        }
      } else {
        switch (this.activeStep) {
          case 2:
            this.activeStep--;
            this.signupService.updateUserStepCompany(
              'register',
              'powers',
              this.routeId,
            );
            break;

          case 1:
            this.activeStep--;

            this.signupService.changeCompanyStepEvent.emit('representatives');

            this.signupService.setComplementaryCompanyStep(
              'complementaryRepresentatives',
            );
            break;

          case 0:
            if (!this.activeSubstep) break;
            switch (this.activeSubstep) {
              case 'complementaryAddress':
                this.signupService.changeCompanyStepEvent.emit('general');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryGeneral',
                );
                break;

              case 'complementaryBank':
                this.signupService.changeCompanyStepEvent.emit('address');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryAddress',
                );
                break;

              case 'complementaryComposition':
                this.signupService.changeCompanyStepEvent.emit('bank');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryBank',
                );
                break;

              case 'complementaryEconomic':
                this.signupService.changeCompanyStepEvent.emit('composition');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryComposition',
                );
                break;

              case 'complementaryBond':
                this.signupService.changeCompanyStepEvent.emit('economic');

                this.signupService.setComplementaryCompanyStep(
                  'complementaryEconomic',
                );
                break;

              case 'complementaryRisk':
                // Não deve acessar etapa de risco se for cedente ou prestador
                const isAssignorRisk = this.router.url.includes('assignor');
                if (isAssignorRisk || this.isProvider) {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                } else {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                }
                break;

              case 'complementaryRepresentatives':
                // Se for cedente ou prestador, pula a etapa de risco
                const isAssignorRep = this.router.url.includes('assignor');
                if (isAssignorRep || this.isProvider) {
                  this.signupService.changeCompanyStepEvent.emit('bond');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryBond',
                  );
                } else {
                  this.signupService.changeCompanyStepEvent.emit('risk');
                  this.signupService.setComplementaryCompanyStep(
                    'complementaryRisk',
                  );
                }
                break;

              default:
                break;
            }

            break;
          default:
            break;
        }
      }
    }
  }

  async proceed() {
    this.signupService.loading = true;

    // TODO mudar essa lógica

    if (this.canProceed()) {
      if (this.isShareholder) {
        if (this.isFund) {
          switch (this.activeStep) {
            case 0:
              const step = this.signupService.getComplementaryCompanyStep();

              if (step !== 'complementaryManager') {
                this.signupService.loading = false;
                this.signupService.sendComplementaryCompanyData.emit(step);
                return;
              }

              await this.signupService.updateUserStepCompany(
                'register',
                'powers',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 1:
              this.signupService.sendCompanyData.emit('powers');

              await this.signupService.updateUserStepCompany(
                'register',
                'declarations',
                this.routeId,
              );
              this.activeStep++; // Ir para o próximo step (tax, que agora é case 2 após remover analysis)

              break;

            case 2:
              this.signupService.sendCompanyData.emit('declarations');
              // Se for cotista pj fundo, pular a etapa de analysis e ir direto para tax

              // Marcar analysis como completo no serviço
              this.signupService.setCompanyFormStatus('analysis', true);
              await this.signupService.updateUserStepCompany(
                'register',
                'tax',
                this.routeId,
              );
              this.activeStep++; // Ir para o próximo step (tax, que agora é case 2 após remover analysis)

              break;

            case 3:
              // Se for fundo, este case é tax (analysis foi removido)
              // Se não for fundo, este case é analysis

              this.signupService.sendCompanyData.emit('tax');
              await this.signupService.updateUserStepCompany(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep++;

              break;

            case 4:
              this.activeStep++;

              this.showLoadingSpinner = true;
              const approvalDone = await this.sendApproval();
              if (approvalDone) {
                await this.signupService.updateUserStepCompany(
                  'review',
                  'general',
                  this.routeId,
                );

                this.router.navigateByUrl(
                  `/app/details/${this.role}/${this.routeId}`,
                );
              }

              break;

            case 5:
              this.router.navigate([`/app/dashboard`]);
              break;
          }
        } else {
          switch (this.activeStep) {
            case 0:
              const step = this.signupService.getComplementaryCompanyStep();

              if (step !== 'complementaryRepresentatives') {
                this.signupService.loading = false;
                this.signupService.sendComplementaryCompanyData.emit(step);
                return;
              }

              await this.signupService.updateUserStepCompany(
                'register',
                'powers',
                this.routeId,
              );
              this.activeStep++;

              break;

            case 1:
              this.signupService.sendCompanyData.emit('powers');
              await this.signupService.updateUserStepCompany(
                'register',
                'income',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 2:
              this.signupService.sendCompanyData.emit('income');
              await this.signupService.updateUserStepCompany(
                'register',
                'declarations',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 3:
              this.signupService.sendCompanyData.emit('declarations');
              await this.signupService.updateUserStepCompany(
                'register',
                'analysis',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 4:
              this.signupService.sendCompanyData.emit('analysis');
              await this.signupService.updateUserStepCompany(
                'register',
                'conditions',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 5:
              this.signupService.sendCompanyData.emit('conditions');
              await this.signupService.updateUserStepCompany(
                'register',
                'tax',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 6:
              this.signupService.sendCompanyData.emit('tax');
              await this.signupService.updateUserStepCompany(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep++;
              break;

            case 7:
              this.activeStep++;

              this.showLoadingSpinner = true;
              const approvalDone = await this.sendApproval();
              if (approvalDone) {
                await this.signupService.updateUserStepCompany(
                  'review',
                  'general',
                  this.routeId,
                );

                this.router.navigateByUrl(
                  `/app/details/${this.role}/${this.routeId}`,
                );
              }

              break;

            case 8:
              this.router.navigate([`/app/dashboard`]);
              break;
          }
        }
      } else {
        switch (this.activeStep) {
          case 0:
            const step = this.signupService.getComplementaryCompanyStep();

            if (step !== 'complementaryRepresentatives') {
              this.signupService.loading = false;
              this.signupService.sendComplementaryCompanyData.emit(step);
              return;
            }

            await this.signupService.updateUserStepCompany(
              'register',
              'powers',
              this.routeId,
            );
            this.activeStep++;

            break;

          case 1:
            await this.signupService.updateUserStepCompany(
              'register',
              'attachments',
              this.routeId,
            );
            this.activeStep++;
            break;

          case 2:
            this.activeStep++;

            this.showLoadingSpinner = true;
            const approvalDone = await this.sendApproval();
            if (approvalDone) {
              await this.signupService.updateUserStepCompany(
                'review',
                'general',
                this.routeId,
              );

              this.router.navigateByUrl(
                `/app/details/${this.role}/${this.routeId}`,
              );
            }

            break;

          case 3:
            this.router.navigate([`/app/dashboard`]);
            break;
        }
      }
    }
    this.signupService.loading = false;
  }

  async setInviteInfo() {
    const user = this.authService.getUser();

    if (!this.routeId) {
      // Fallback para active_register se não tiver register_id na URL
      const invite = user.active_register?.register?.invite;

      if (invite) {
        this.name = invite.data.name ? invite.data.name.toUpperCase() : '';
        this.label = await this.getRoleLabel(this.role);

        // Define basicInfo mesmo sem routeId
        this.signupService.basicInfo = {
          name: invite.data.name,
          email: invite.data.email,
          document: invite.data.document,
        };
      }
      return;
    }

    try {
      // Buscar dados do register pela API
      const response = await this.apiService.get<any>({
        route: `api/registration/register/${this.routeId}`,
        token: true,
      });

      const register = response?.data;
      const invite = register?.invite;

      if (!invite) {
        // Tentar usar active_register como fallback
        const fallbackInvite = user.active_register?.register?.invite;
        if (fallbackInvite && fallbackInvite.data) {
          this.label = await this.getRoleLabel(this.role);
          this.name = fallbackInvite.data.name
            ? fallbackInvite.data.name.toUpperCase()
            : '';
          this.signupService.basicInfo = {
            name: fallbackInvite.data.name,
            email: fallbackInvite.data.email,
            document: fallbackInvite.data.document,
          };
        }
        return;
      }

      // Se invite é apenas um número (ID), usar active_register
      let inviteData = null;
      if (typeof invite === 'number') {
        const fallbackInvite = user.active_register?.register?.invite;
        if (fallbackInvite && fallbackInvite.data) {
          inviteData = fallbackInvite.data;
        } else {
          return;
        }
      } else if (invite.data) {
        // Se invite é um objeto com data
        inviteData = invite.data;
      } else {
        return;
      }

      if (!inviteData || !inviteData.name) {
        return;
      }

      this.label = await this.getRoleLabel(this.role);

      this.name = inviteData.name ? inviteData.name.toUpperCase() : '';

      this.signupService.basicInfo = {
        name: inviteData.name,
        email: inviteData.email,
        document: inviteData.document,
      };
    } catch (error) {
      console.error(
        'Erro ao buscar dados do register para setInviteInfo:',
        error,
      );
      // Fallback para active_register em caso de erro
      const invite = user.active_register?.register?.invite;
      if (invite) {
        this.name = invite.data.name ? invite.data.name.toUpperCase() : '';
        this.label = await this.getRoleLabel(this.role);

        // Define basicInfo no fallback também
        this.signupService.basicInfo = {
          name: invite.data.name,
          email: invite.data.email,
          document: invite.data.document,
        };
      }
    }
  }

  getDisabled() {
    try {
      if (!this.signupService) {
        return true;
      }
      return !this.canProceed() || this.signupService.loading;
    } catch (error) {
      return true;
    }
  }

  toggleSupportModal() {
    this.showSupportModal = !this.showSupportModal;
  }

  configureStepsForFund() {
    if (!this.isShareholder || !this.isFund) {
      return;
    }

    // TODO - precisa deixar os steps dinâmicos
    const stepsToExclude: CompanyFormKeys[] = [
      'complementaryComposition',
      'complementaryEconomic',
      'income',
      'conditions',
      'analysis', // Excluir etapa de análise para cotista pj fundo
    ];

    this.steps = this.steps.filter((step) => {
      if (step.slug === 'complementary') {
        step.substeps = step.substeps.filter((substep) => {
          return !stepsToExclude.includes(substep.slug as CompanyFormKeys);
        });
      }

      return !stepsToExclude.includes(step.slug as CompanyFormKeys);
    });

    stepsToExclude.forEach((step) => {
      this.signupService.setCompanyFormStatus(step, true);
    });

    const taxIndex = this.steps.findIndex((step) => step.slug === 'tax');

    if (taxIndex !== -1) {
      this.steps[taxIndex].title =
        'Declaração US Person / Non US Person (FATCA)';
    }

    // Removido: step de analysis será excluído para cotista pj fundo

    // Verificar se os steps de Admin e Manager já foram adicionados
    const hasAdmin = this.steps[0]?.substeps?.some(
      (substep) => substep.slug === 'complementaryAdmin',
    );
    const hasManager = this.steps[0]?.substeps?.some(
      (substep) => substep.slug === 'complementaryManager',
    );

    // Adicionar apenas os steps que faltam
    if (!hasAdmin) {
      this.steps[0].substeps.push({
        title: 'Administrador',
        slug: 'complementaryAdmin',
      });
    }

    if (!hasManager) {
      this.steps[0].substeps.push({
        title: 'Gestor',
        slug: 'complementaryManager',
      });
    }
  }

  async updateIsFundFromRegisterId(registerId: string) {
    try {
      // Buscar informações do register através do required-files que retorna o role
      const response = await this.apiService.get<any>({
        route: `api/required-files/${registerId}`,
        token: true,
      });

      if (response?.data?.register?.role?.slug) {
        const wasFund = this.isFund;
        this.isFund =
          response.data.register.role.slug.includes('PJ-FI') || false;

        // Atualizar o valor no serviço
        this.signupService.setIsFund(this.isFund);

        // Se isFund mudou para true e é shareholder, configurar os steps
        if (this.isFund && !wasFund && this.isShareholder) {
          this.configureStepsForFund();
        }
      }
    } catch (error) {
      // Em caso de erro, manter o valor atual (false se hasMultipleAccounts)
      console.error('Erro ao buscar role do register:', error);
    }
  }

  async logoff() {
    // Limpar tudo de forma assíncrona e aguardar conclusão
    await this.authService.clearUser();

    // Verificar uma última vez se tudo foi limpo
    const userStillExists = localStorage.getItem('@app:user');
    const tokenStillExists = localStorage.getItem('@app:token');

    if (userStillExists || tokenStillExists) {
      // Se ainda existir, limpar manualmente
      if (userStillExists) localStorage.removeItem('@app:user');
      if (tokenStillExists) localStorage.removeItem('@app:token');

      // Aguardar um pouco mais para garantir
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Navegar com replaceUrl para evitar problemas de histórico
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}
